import React, { useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { get, isEmpty, map, omit, isNil } from "lodash";
import { Form, Col } from "react-bootstrap";
import { FormLabelStyle } from "../../form/FormLabel";
import { MediaRequestContext } from "../../../context/MediaRequestContext";
import {
  StyledBlackButton,
  StyledDarkBlueButton,
  StyledBlueButton,
} from "../../../styles/BootstrapStyles";
import "./createMediaRequest.scss";
import {
  Select,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Step,
  Stepper,
  StepLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  CREATE_MEDIA,
  SUMMARY_TABLE_HEADER,
  SUMMARY_TABLE_VALUE_KEYS,
  AUDIO_CONTENT_KIND_MAPPING,
  TEXT_USAGE_TYPE,
  SUPPLEMENTARY_ASSET_TYPE,
} from "../../../constants/CreateMediaRequest";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { ReactComponent as AWSS3Logo } from "../../../assets/icons/awss3.svg";
import Video from "./Video";
import Audio from "./Audio";
import Subtitles from "./Subtitles";
import DubCards from "./DubCards";
import ConfigUtils from "../../../utils/ConfigUtils";
import { getEditRate } from "./util";
import { PageErrorContent } from "../../page-error/PageError";
import ProxyVideo from "./ProxyVideo";
import { MEDIA_SEARCH } from "../../../constants/MediaSearch";
import { getCantemoPageCount, getCantemoPageKeys, isCantemoValidAssets } from "../../../utils/MediaRequestUtils";
import LoadingSpinner from "../../common/LoadingSpinner";

const CreateMediaRequest = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    validAssets,
    _setVideoContainerId,
    videoContainerId,
    activeStep,
    _setActiveStep,
    selectedFiles,
    _setIdAccordionExpanded,
    idAccordionExpanded,
    _setTitleAccordionExpanded,
    titleAccordionExpanded,
    _setTransferRegion,
    transferRegion,
    _setSelectedFiles,
    submitMediaRequest,
    requestName,
    _setRequestName,
    saveToCart,
    _setSaveToCart,
    isMrSubmitError,
    _setIsMrSubmitError,
    getAssetType,
    renamedFilename,
    _setRenamedFilename,
    getSourceId,
    cantemoPage = 0,
    cantemoTotal = null,
    _setCantemoPage = () => {},
    searchCantemoPage,
    isCantemoLoading = false,
  } = useContext(MediaRequestContext);
  const regionDetails = ConfigUtils.getTransferRegions();

  useEffect(() => {
    if (isEmpty(validAssets)) {
      if (pathname !== "/mediaRequest" && isEmpty(saveToCart)) {
        history.push("/mediaRequest");
      }
    }
  }, [history, validAssets, pathname, saveToCart]);  

  const getEditsAssetTitleDetails = (asset) => {
    const title = get(asset, "displayTitle", "");
    
    const episode = get(asset, "episodeNumber", "");
    return episode && episode !== "0" ? `${title} (Episode ${episode})` : title;
  };

  const getAdditionalVideoFilenameDetails = (manifestation, filename, container, type) => {
    let filenameDetails = "";
    manifestation.components?.forEach((comp) => {
      const size = comp.channels?.length;
      const audioContentKind =
        AUDIO_CONTENT_KIND_MAPPING[comp.audioContentKind] || "Unknown";
      const language = comp.spokenLanguage || "Unknown";
      filenameDetails = size
        ? `${size}CH_${audioContentKind.replace(/\s+/g, "")}_${language}`
        : `${audioContentKind.replace(/\s+/g, "")}_${language}`;
      if (
        comp.componentType?.includes("audio") &&
        (comp.channels?.[0]?.trackReference?.containerId ===
          container.containerId || comp.trackReference?.containerId ===
          container.containerId)
      ) {
        filename = `${filename}_${filenameDetails}`;
      }
    });
    if (type === "Proxy Video") {
      filename = `${filename}_proxy`;
    }
    return filename;
  };

  const getAdditionalAudioFilenameDetails = (manifestation, filename, container) => {
    let filenameDetails = "";
    manifestation.components?.forEach((comp) => {
      const size = comp.channels?.length;
      const audioContentKind =
        AUDIO_CONTENT_KIND_MAPPING[comp.audioContentKind] || "Unknown";
      const language = comp.spokenLanguage || "Unknown";
      filenameDetails = size
        ? `${size}CH_${audioContentKind.replace(/\s+/g, "")}_${language}`
        : `${audioContentKind.replace(/\s+/g, "")}_${language}`;
      if (
        comp.componentType?.includes("audio") &&
        (comp.channels?.[0]?.trackReference?.containerId ===
          container.containerId || comp.trackReference?.containerId ===
          container.containerId)
      ) {
        filename = `${filename}_${filenameDetails}`;
      }
    });
    return filename;
  };

  const updateFilename = (manifestation, assetTitle, entityType, idType, editRate) => {
    let renamedFiles = {...renamedFilename};
    manifestation.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        if (idType === "dmi" || idType === "cantemo" || idType === "bim") {
          manifestation.components?.forEach((component) => {
            if (component.componentType?.includes("video") && component.trackReference?.containerId === container.containerId) {
              renamedFiles = { ...renamedFiles, [file] : file };
            }
            else if (component.componentType?.includes("timed-text") && component.trackReference?.containerId === container.containerId) {
              renamedFiles = { ...renamedFiles, [file] : file };
            }
            else if (component.componentType?.includes("audio") && component.trackReference?.containerId === container.containerId) {
              renamedFiles = { ...renamedFiles, [file] : file };
            }
          });
        } 
        else {
          const assetType = getAssetType(entityType);
          let filename = isNil(assetType)
            ? `${assetTitle.replace(/\s+/g, "")}_${editRate}`
            : `${assetTitle.replace(/\s+/g, "")}_${assetType}_${editRate}`;
          for(const component of manifestation.components) {
            if ( component.componentType?.includes("video") && component.trackReference?.containerId === container.containerId ) {
              filename = getAdditionalVideoFilenameDetails(manifestation, filename, container, "Video");
              renamedFiles = { ...renamedFiles, [file] : `${filename}.${format}`};
              break;
            }
            else if (component.componentType?.includes("timed-text") && component.trackReference?.containerId === container.containerId) {
              filename = isNil(assetType)
                ? `${assetTitle.replace(/\s+/g, "")}`
                : `${assetTitle.replace(/\s+/g, "")}_${assetType}`;
              const textUsageType = TEXT_USAGE_TYPE[component.textUsageType] || "Unknown";
              const language = component.writtenLanguage || "Unknown";
              const filenameDetails = `${textUsageType.replace(/\s+/g, '')}_${language}`;
              renamedFiles = { ...renamedFiles, [file] : `${filename}_${filenameDetails}.${format}`};
              break;
            }
            else if (
              component.componentType?.includes("audio") &&
              (component.channels?.[0]?.trackReference?.containerId ===
                container?.containerId ||
                component.trackReference?.containerId === container?.containerId) &&
              !videoContainerId[container?.containerId]
            ) {
              filename = getAdditionalAudioFilenameDetails(manifestation, filename, container);
              renamedFiles = { ...renamedFiles, [file]: `${filename}.${format}` };
              break;
            }
          }
          manifestation.supplementaryAssets?.forEach((supplementaryAsset) => {
            if (
              supplementaryAsset.supplementaryAssetType?.includes("image") &&
              supplementaryAsset.containerReference?.containerId ===
                container.containerId
            ) {
              filename = isNil(assetType)
                ? `${assetTitle.replace(/\s+/g, "")}`
                : `${assetTitle.replace(/\s+/g, "")}_${assetType}`;
              const supplementaryAssetType =
                SUPPLEMENTARY_ASSET_TYPE[supplementaryAsset.supplementaryAssetType];
              const language = supplementaryAsset.writtenLanguage || "Unknown";
              const sequence = supplementaryAsset.sequence;
              const filenameDetails = `${supplementaryAssetType.replace(
                /\s+/g,
                ""
              )}_${language}_${sequence}`;
              renamedFiles = {
                ...renamedFiles,
                [file]: `${filename}_${filenameDetails}.${format}`,
              };
            }
          });
        }
      }
    });
    manifestation.mamProxy?.containers?.forEach((proxyContainer) => {
      if (proxyContainer.path) {
        const pathSplit = proxyContainer.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${proxyContainer.containerId}.${format}`;
        const assetType = getAssetType(entityType);
        let proxyFilename = !isNil(assetType)
          ? `${assetTitle.replace(/\s+/g, "")}_${assetType}_${editRate}`
          : `${assetTitle.replace(/\s+/g, "")}_${editRate}`;
        manifestation.mamProxy?.components?.forEach((proxyComponent) => {
          if (proxyComponent.componentType?.includes("video") && proxyComponent.trackReference?.containerId === proxyContainer.containerId) {
            proxyFilename = getAdditionalVideoFilenameDetails(manifestation.mamProxy, proxyFilename, proxyContainer, "Proxy Video");
            renamedFiles = { ...renamedFiles, [file]: `${proxyFilename}.${format}` };
          }
        });
      }
    });
    return renamedFiles;
  };

  const showCantemoPagination = isCantemoValidAssets(validAssets);
  const cantemoPageCount = getCantemoPageCount(validAssets, cantemoTotal);
  const displayedAssetKeys = showCantemoPagination && cantemoTotal == null
    ? getCantemoPageKeys(validAssets, cantemoPage)
    : Object.keys(validAssets);
  const goToCantemoPage = (page) => {
    if (cantemoTotal != null && searchCantemoPage) {
      searchCantemoPage(page);
      return;
    }
    _setCantemoPage(page);
  };

  useEffect(() => {
    const videoAttachedAudio = { ...videoContainerId };
    const idAccordionStructure = {};
    const titleAccordionStructure = {};
    const transferRegionStructure = {};
    let updatedRenamedFiles = {};
    displayedAssetKeys.forEach((validAssetsKey) => {
      const manifestationObj = validAssets[validAssetsKey]?.manifestation;
      const assetTitle = getEditsAssetTitleDetails(validAssets[validAssetsKey]);
      const entityType = validAssets[validAssetsKey].entityType;
      const editRate = getEditRate(manifestationObj.editRate);
      const renamedValue = updateFilename(manifestationObj, assetTitle, entityType, validAssets[validAssetsKey]?.idType, editRate);
      updatedRenamedFiles = {...updatedRenamedFiles, ...renamedValue};
      titleAccordionStructure[validAssetsKey] = true;
      if (!isEmpty(manifestationObj)) {
        idAccordionStructure[validAssetsKey] = true;
        transferRegionStructure[validAssetsKey] = "WBDShare";
        if (manifestationObj?.mamProxy) {
          transferRegionStructure[manifestationObj.mamProxy.id] = "WBDShare";
        }
        manifestationObj?.components?.forEach(
          (component) => {
            if (
              component.componentType?.includes("video") &&
              component.trackReference?.containerId
            ) {
              videoAttachedAudio[component.trackReference.containerId] = true;
            }
          }
        );
        _setVideoContainerId(videoAttachedAudio);
      }
    });
    if (showCantemoPagination && cantemoPage > 0) {
      _setRenamedFilename((prev) => ({ ...prev, ...updatedRenamedFiles }));
      _setTitleAccordionExpanded((prev) => ({ ...prev, ...titleAccordionStructure }));
      _setIdAccordionExpanded((prev) => ({ ...prev, ...idAccordionStructure }));
      _setTransferRegion((prev) => ({ ...prev, ...transferRegionStructure }));
    } else {
      _setRenamedFilename(updatedRenamedFiles);
      _setTitleAccordionExpanded(titleAccordionStructure);
      _setIdAccordionExpanded(idAccordionStructure);
      _setTransferRegion(transferRegionStructure);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validAssets, cantemoPage]);

  const getEditAssetTitle = (asset) => {
    const title = getEditsAssetTitleDetails(asset);
    const format = get(asset, "manifestation.format", "");

    return (
      <>
        <div className="accordion-title">{title}</div>
        {format? <div className="accordion-title">Format: {format}</div>: null}
      </>
    );
  };

  const handleTransferRegionChange = (e, manifestationObj) => {
    const newTransferValue = { ...transferRegion };
    const refreshedSelectedFiles = { ...selectedFiles };
    const alternateId = getSourceId(manifestationObj);
    const applyRegionToSelectedFile = (file, container, regionName) => {
      refreshedSelectedFiles[file]["transfer"] = regionName;
      if (regionName === "GCSSNY-MX_Content App") {
        refreshedSelectedFiles[file]["sourceUri"] = container.path;
        refreshedSelectedFiles[file]["containerId"] = container.containerId;
      } else {
        delete refreshedSelectedFiles[file]["sourceUri"];
        delete refreshedSelectedFiles[file]["containerId"];
      }
    };
    if (manifestationObj.mamProxy) {
      const mamProxyAlternateId = getSourceId(manifestationObj.mamProxy);
      newTransferValue[manifestationObj.mamProxy.id] = e.target.value;
      manifestationObj.mamProxy?.containers?.forEach((container) => {
        if (container.path) {
          const pathSplit = container.path?.split(".");
          const format = pathSplit[pathSplit.length - 1];
          const file = `${container.containerId}.${format}`;
          manifestationObj.mamProxy?.components?.forEach((component) => {
            if (
              file in selectedFiles &&
              (selectedFiles[file]["id"] === mamProxyAlternateId ||
                selectedFiles[file]["id"] === manifestationObj.mamProxy.id)
            ) {
              applyRegionToSelectedFile(file, container, newTransferValue[manifestationObj.mamProxy.id]);
            }
          });
        }
      });
    }
    const manifestationId = manifestationObj?.id;
    newTransferValue[manifestationId] = e.target.value;
    manifestationObj.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        manifestationObj.components?.forEach((component) => {
          if (
            file in selectedFiles &&
            selectedFiles[file]["id"] === alternateId
          ) {
            applyRegionToSelectedFile(file, container, newTransferValue[manifestationId]);
          }
        });
      }
    });

    _setTransferRegion(newTransferValue);
    _setSelectedFiles(refreshedSelectedFiles);
  };

  const renderTransferDropdown = (manifestationObj) => {
    const manifestationId = manifestationObj.id;
    return (
      <div className="create-media-region-dropdown">
        <span>
          <AWSS3Logo className="aws-s3-logo" />
        </span>
        <Form.Group
          className="m-0 p-0 create-media-form-group"
          as={Col}
          controlId="region"
        >
          <Form.Label className="formLabel">
            {CREATE_MEDIA.regionLabel}
          </Form.Label>
          <Form.Control
            as={Select}
            disableUnderline={true}
            displayEmpty
            required
            classes={{
              disabled: "formSelectDisabled",
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              style: {
                zIndex: 15000,
              },
            }}
            defaultValue={get(regionDetails[0], "name")}
            value={transferRegion[manifestationId]}
            onChange={(e) => handleTransferRegionChange(e, manifestationObj)}
            className={"formInputSelect"}
          >
            {map(regionDetails, (region, index) => {
              return (
                <MenuItem
                  className="dropdownSelect"
                  key={index}
                  value={get(region, "name")}
                >
                  {get(region, "name")}
                </MenuItem>
              );
            })}
          </Form.Control>
        </Form.Group>
      </div>
    );
  };

  const handleDeleteFile = (file) => {
    let savedFilesInCart = { ...saveToCart };
    let savedSelectedFiles = { ...selectedFiles };
    savedFilesInCart = omit(savedFilesInCart, file);
    savedSelectedFiles = omit(savedSelectedFiles, file);
    _setSelectedFiles(savedSelectedFiles);
    _setSaveToCart(savedFilesInCart);
    if (isEmpty(savedFilesInCart)) {
      handleReset();
      history.push("/mediaRequest");
    }
  };

  const renderTableCells = (file) =>
    SUMMARY_TABLE_VALUE_KEYS.map((key, index) => {
      const value = get(saveToCart[file], key, "");
      return (
        <TableCell
          className="summary-cell-font"
          key={`${value}-${index}`}
          title={value}
        >
          <p
            className="summary-cell-width"
            style={{
              width: key === "renamedFilename" ? 320 : key === "title" ? 180 : 120,
            }}
          >
            {value}
          </p>
        </TableCell>
      );
    });

  const renderTableRow = () =>
    Object.keys(saveToCart).map((file) => (
      <TableRow key={file}>
        {renderTableCells(file)}
        <TableCell
          className="summary-cell-font"
          title={`delete ${file}`}
        >
          <p
            className="summary-cell-width"
            style={{
              width: 30,
            }}
          >
            <IconButton
              className="delete-file-icon-button"
              onClick={() => handleDeleteFile(file)}
            >
              <DeleteIcon className="delete-icon" />
            </IconButton>
          </p>
        </TableCell>
      </TableRow>
    ));

  const getSteps = () => {
    return [CREATE_MEDIA.pageHeader, CREATE_MEDIA.summaryHeader];
  };

  const renderSelectedAssetsAccordions = (validAssetsKey, manifestationObj, idType = null) => {
    const alternateId = getSourceId(manifestationObj);
    const manifestationId = manifestationObj?.id;
    const assetTitle = getEditsAssetTitleDetails(validAssets[validAssetsKey]);
    const entityType = validAssets[validAssetsKey].entityType;
    const editRate = getEditRate(manifestationObj.editRate);
    let videoFilename = "";
    let videoFound = false;
    for (const container of manifestationObj.containers) {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const assetType = getAssetType(entityType);
        let filename = !isNil(assetType) ? `${assetTitle.replace(/\s+/g, '')}_${assetType}_${editRate}`: `${assetTitle.replace(/\s+/g, '')}_${editRate}`;
        // eslint-disable-next-line no-loop-func
        manifestationObj.components?.forEach((component) => {
          if ( component.componentType?.includes("video") && component.trackReference?.containerId === container.containerId) {
            if (idType === "dmi" || idType === "cantemo" || idType === "bim") {
              videoFilename = `${container.containerId}.${format}`;
              videoFound = true;
            }
            else{
              let filenameDetails = "";
              manifestationObj.components?.forEach((comp) => {
                const size = comp.channels?.length;
                const audioContentKind = AUDIO_CONTENT_KIND_MAPPING[comp.audioContentKind] || "Unknown";
                const language = comp.spokenLanguage || "Unknown";
                filenameDetails = size ? `${size}CH_${audioContentKind.replace(/\s+/g, '')}_${language}`: `${audioContentKind.replace(/\s+/g, '')}_${language}`;
                if(comp.componentType?.includes("audio") && comp.channels?.[0]?.trackReference?.containerId === container.containerId) {
                  filename = `${filename}_${filenameDetails}`;
                }
              });
              videoFilename = `${filename}.${format}`;
            }
          }
        });
        if (videoFound) {
          break;
        }
      }
    }
    let summaryTitle: string = alternateId;
    if ((videoFound && (idType === "dmi" || idType === "cantemo" || idType === "bim")) || (idType !== "dmi" && idType !== "cantemo" && idType !== "bim")) {
      summaryTitle = `${alternateId} - ${videoFilename}`;
    }
    
    return (
      <Accordion
        classes={{
          expanded: "media-accordion",
        }}
        className="media-assets-accordion"
        key={`accordion-${manifestationId}`}
        variant="outlined"
        expanded={idAccordionExpanded[validAssetsKey]}
        onChange={(e, expanded) => {
          const newValue = { ...idAccordionExpanded };
          newValue[validAssetsKey] = expanded;
          _setIdAccordionExpanded(newValue);
        }}
      >
        <AccordionSummary
          className="media-asset-accordion-summary word-break-all"
          expandIcon={<ExpandMoreIcon />}
        >
          {summaryTitle}
        </AccordionSummary>
        <AccordionDetails className="media-asset-accordion-details">
          <Video manifestation={manifestationObj} assetTitle={assetTitle} entityType={entityType} idType={idType} />
          <Audio manifestation={manifestationObj} assetTitle={assetTitle} entityType={entityType} idType={idType} />
          <Subtitles manifestation={manifestationObj} assetTitle={assetTitle} entityType={entityType} idType={idType} />
          <DubCards manifestation={manifestationObj} assetTitle={assetTitle} entityType={entityType} />
          <ProxyVideo manifestation={manifestationObj["mamProxy"]} assetTitle={assetTitle} entityType={entityType} idType={idType} />
        </AccordionDetails>
      </Accordion>
    );
  };

  const getOdsStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <>
            <div className="cantemo-results">
              {isCantemoLoading && (
                <LoadingSpinner show={isCantemoLoading} />
              )}
              {displayedAssetKeys.map((validAssetsKey, index) => (
              <Accordion
                classes={{
                  expanded: "media-accordion",
                }}
                className="media-assets-accordion"
                key={`accordion-${index}-${validAssetsKey}-${titleAccordionExpanded[validAssetsKey]}`}
                variant="outlined"
                expanded={titleAccordionExpanded[validAssetsKey]}
                onChange={(e, expanded) => {
                  const newValue = { ...titleAccordionExpanded };
                  newValue[validAssetsKey] = expanded;
                  _setTitleAccordionExpanded(newValue);
                }}
              >
                <AccordionSummary
                  className="media-asset-accordion-summary"
                  expandIcon={<ExpandMoreIcon />}
                >
                  {getEditAssetTitle(validAssets[validAssetsKey])}
                </AccordionSummary>
                <AccordionDetails className="media-asset-accordion-details">
                  <div className="mb-10">
                    {validAssets[validAssetsKey]?.manifestation &&
                      validAssets[validAssetsKey]?.manifestation?.files?.length &&
                      renderTransferDropdown(validAssets[validAssetsKey].manifestation)}
                  </div>
                  {isEmpty(validAssets[validAssetsKey]?.manifestation) && (
                    <div className="media-coming-soon">
                      {CREATE_MEDIA.mediaComingSoonLabel}
                    </div>
                  )}
                  {!isEmpty(validAssets[validAssetsKey]?.manifestation) &&
                    renderSelectedAssetsAccordions(validAssetsKey, validAssets[validAssetsKey]?.manifestation, validAssets[validAssetsKey]?.idType)
                  }
                </AccordionDetails>
                </Accordion>
              ))}
              {showCantemoPagination && cantemoPageCount > 1 && (
                <div className="cantemo-results-pagination">
                  <StyledBlackButton
                    className="cantemo-page-button"
                    disabled={isCantemoLoading || cantemoPage <= 0}
                    onClick={() => goToCantemoPage(cantemoPage - 1)}
                  >
                    {MEDIA_SEARCH.cantemoPrev}
                  </StyledBlackButton>
                  <StyledBlackButton
                    className="cantemo-page-button"
                    disabled={isCantemoLoading || cantemoPage >= cantemoPageCount - 1}
                    onClick={() => goToCantemoPage(cantemoPage + 1)}
                  >
                    {MEDIA_SEARCH.cantemoNext}
                  </StyledBlackButton>
                </div>
              )}
            </div>
          </>
        );
      case 1:
        return (
          <>
            <Form>
              <Form.Group
                className="mr-4 p-0"
                as={Col}
                controlId="requestName"
                xs={6}
              >
                <Form.Label className="formLabel">
                  {<FormLabelStyle label={"Request Name"} impField={true} />}
                </Form.Label>
                <Form.Control
                  className="formInput"
                  autoComplete="off"
                  placeholder="Request Name"
                  onChange={(e) => {
                    e.preventDefault();
                    _setRequestName(e.target.value);
                  }}
                  value={requestName}
                  required
                />
              </Form.Group>
            </Form>
            <Table>
              <TableHead className="selected-assets-summary-table-header">
                <TableRow>
                  {SUMMARY_TABLE_HEADER.map((item, index) => (
                    <TableCell
                      className="summary-cell"
                      key={`${item}-${index}`}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{saveToCart && renderTableRow()}</TableBody>
            </Table>
          </>
        );
    }
  };

  const handleNext = () => {
    let savedFilesInCart = { ...saveToCart };
    Object.keys(selectedFiles).forEach((fileKey) => {
      if (saveToCart.hasOwnProperty(fileKey) && !selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart = omit(savedFilesInCart, fileKey)
      } else if (saveToCart.hasOwnProperty(fileKey) && selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart[fileKey] = selectedFiles[fileKey]
      } else if (!(saveToCart.hasOwnProperty(fileKey)) && selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart = { ...savedFilesInCart, [fileKey]: selectedFiles[fileKey]}
      }
    })
    _setSaveToCart(savedFilesInCart);
    _setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleSubmit = () => {
    submitMediaRequest();
  };

  const handleGoToCart = () => {
    _setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleReset = () => {
    _setIsMrSubmitError(false);
    _setActiveStep(0);
  };

  const handleSaveToCart = () => {
    let savedFilesInCart = { ...saveToCart };
    Object.keys(selectedFiles).forEach((fileKey) => {
      if (saveToCart.hasOwnProperty(fileKey) && !selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart = omit(savedFilesInCart, fileKey)
      } else if (saveToCart.hasOwnProperty(fileKey) && selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart[fileKey] = selectedFiles[fileKey]
      } else if (!(saveToCart.hasOwnProperty(fileKey)) && selectedFiles[fileKey]["isChecked"]) {
        savedFilesInCart = { ...savedFilesInCart, [fileKey]: selectedFiles[fileKey]}
      }
    })
    _setSaveToCart(savedFilesInCart);
    history.push("/mediaRequest");
  };

  const isFilesSelected = !!Object.keys(selectedFiles).length && Object.keys(selectedFiles).some(file => selectedFiles[file].isChecked);
  
  return (
    <div className="selected-assets">
      {
        <>
          <div className="selected-assets-button-links">
            <Button
              color="primary"
              startIcon={<ArrowBackIosIcon />}
              className="mt-40 back-to-media-request"
              size="small"
              onClick={() => {
                handleReset();
                history.push("/mediaRequest");
              }}
            >
              <span style={{ marginLeft: -8 }}>
                {CREATE_MEDIA.backButtonLabel}
              </span>
            </Button>
            {!isEmpty(saveToCart) && activeStep === 0 && (
              <Button
                color="primary"
                endIcon={<ArrowForwardIosIcon />}
                className="mt-40 go-to-cart"
                size="small"
                onClick={handleGoToCart}
              >
                <span style={{ marginRight: -8 }}>
                  {CREATE_MEDIA.forwardButtonLabel}
                </span>
              </Button>
            )}
          </div>
          <div className="create-assets-heading">
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              className="selected-asset-stepper"
            >
              {getSteps().map((label) => (
                <Step key={label} className="selected-asset-step">
                  <StepLabel className="selected-asset-step-label">
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            {isMrSubmitError ? (
            <div className="create-media-request-error">
              <PageErrorContent
                title="Error occured while submitting the media request"
                message="Few IDs are invalid. Please re-select only valid IDs."
                reloadBtn={false}
              />
            </div>
          ) : null}
          </div>
          <div>
            <div className="stepper-buttons">
              {activeStep === getSteps().length - 1 ? (
                <StyledDarkBlueButton
                  className="create-media-submit-button"
                  onClick={handleSubmit}
                  disabled={!requestName || isMrSubmitError}
                >
                  Submit
                </StyledDarkBlueButton>
              ) : (
                <>
                  <StyledDarkBlueButton
                    className="create-media-next-button"
                    onClick={handleNext}
                    disabled={!isFilesSelected}
                  >
                    Next
                  </StyledDarkBlueButton>
                  {isFilesSelected && (
                    <StyledBlueButton
                      className="create-media-save-to-cart-button"
                      onClick={handleSaveToCart}
                    >
                      Save to Cart
                    </StyledBlueButton>
                  )}
                </>
              )}
              {activeStep > 0 ? (
                <StyledBlackButton
                  onClick={handleReset}
                  disabled={isEmpty(validAssets)}
                >
                  Cancel
                </StyledBlackButton>
              ) : null}
            </div>
            {getOdsStepContent(activeStep)}
          </div>
        </>
      }
    </div>
  );
};

export default CreateMediaRequest;
